import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  extractAdminPortalList,
  useAdminModulePermission,
} from "../../../../utils/adminPortalHelpers";
import {
  showErrorMsg,
  showSuccessMsg,
  uploadFileWithProgress,
} from "../../../../utils/Helpers";
import {
  createAdminCompanyUser,
} from "../../../../network/apis/adminCompanyUsers";
import { getAdminCompanyRoles } from "../../../../network/apis/adminCompanyRoles";
import { splitPhoneForApi } from "../../../Company-portal/TeamsPermissions/employeeMappers";
import { normalizeCompanyRoles } from "../../../Company-portal/TeamsPermissions/roleMappers";
import {
  adminCompanyTeamRoutes,
  COMPANY_DETAILS_TEAM_TAB,
  TEAM_SUB_TABS,
} from "../adminCompanyTeamRoutes";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const useAdminAddEmployee = () => {
  const history = useHistory();
  const { companyId } = useParams();
  const lang = useSelector((state) => state.locale.lang);
  const teamCopy = messages[lang].teamManagement;
  const copy = teamCopy.addEmployee;
  const companiesList = messages[lang].companiesList;
  const { shared } = messages[lang];
  const { canAdd } = useAdminModulePermission(
    companyPermissionModules.employees,
  );

  const [roleOptions, setRoleOptions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountryDialCode, setPhoneCountryDialCode] = useState("966");
  const [role, setRole] = useState(null);
  const [pictureFile, setPictureFile] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);
  const [uploadState, setUploadState] = useState({ target: null, percent: 0 });
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState({});

  const goBackToList = useCallback(() => {
    if (!companyId) {
      history.push(ROUTE_PATHS.companiesList);
      return;
    }
    history.push(
      adminCompanyTeamRoutes.companyDetailsTabLocation(
        companyId,
        COMPANY_DETAILS_TEAM_TAB,
        TEAM_SUB_TABS.employees,
      ),
    );
  }, [history, companyId]);

  useEffect(() => {
    if (!companyId) {
      goBackToList();
      return undefined;
    }

    let cancelled = false;
    getAdminCompanyRoles({ company_id: companyId, page: 1, items: 100 })
      .then((response) => {
        if (cancelled) return;
        setRoleOptions(normalizeCompanyRoles(extractAdminPortalList(response)));
      })
      .catch((error) => {
        if (cancelled) return;
        showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, goBackToList, shared.generalErrMsg, lang]);

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();

  const errors = useMemo(() => {
    const next = {};
    if (!trimmedName) next.name = copy.validations.nameRequired;
    if (!trimmedEmail) next.email = copy.validations.emailRequired;
    else if (!EMAIL_REGEX.test(trimmedEmail))
      next.email = copy.validations.emailInvalid;
    if (!role) next.role = copy.validations.roleRequired;
    return next;
  }, [trimmedName, trimmedEmail, role, copy.validations]);

  const isFormValid = useMemo(
    () => Object.keys(errors).length === 0,
    [errors],
  );

  const showError = (field) =>
    Boolean((submitted || touched[field]) && errors[field]);

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const uploadingPicture = uploadState.target === "profile_picture";
  const busy = creating || uploadingPicture;

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!isFormValid || busy || !companyId) return;

    let profilePictureUrl;
    if (pictureFile) {
      try {
        profilePictureUrl = await uploadFileWithProgress(
          pictureFile,
          "profile_picture",
          "employee",
          setUploadState,
        );
      } finally {
        setUploadState({ target: null, percent: 0 });
      }
      if (!profilePictureUrl) return;
    }

    const data = {
      company_id: companyId,
      name: trimmedName,
      email: trimmedEmail,
      company_role_id: Number(role.id),
    };
    const { dial_code, phone_number } = splitPhoneForApi(
      phone,
      phoneCountryDialCode,
    );
    if (phone_number) {
      data.phone_number = phone_number;
      data.dial_code = dial_code;
    }
    if (profilePictureUrl) {
      data.profile_picture_url = profilePictureUrl;
    }

    setCreating(true);
    try {
      await createAdminCompanyUser(data);
      showSuccessMsg(copy.createSuccess, true);
      goBackToList();
    } catch (error) {
      showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
    } finally {
      setCreating(false);
    }
  };

  const breadcrumbList = [
    { path: ROUTE_PATHS.companiesList, label: companiesList.listBreadcrumb },
    {
      path: adminCompanyTeamRoutes.companyDetailsTeamTab(companyId),
      label: companiesList.teamPermissionsTab,
    },
    { path: "", label: copy.title },
  ];

  return {
    copy,
    breadcrumbList,
    name,
    setName,
    email,
    setEmail,
    phone,
    setPhone,
    setPhoneCountryDialCode,
    role,
    setRole,
    roleOptions,
    pictureFile,
    picturePreview,
    setPictureFile,
    setPicturePreview,
    uploadState,
    uploadingPicture,
    busy,
    showError,
    markTouched,
    errors,
    isFormValid,
    goBackToList,
    handleSubmit,
    canAdd,
  };
};

export default useAdminAddEmployee;
